import { showToast } from '@eptai/eptai-app-components';
import axios, { AxiosError, AxiosHeaders, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { apiClient } from './apiClient';
import { redirectToAppLogin } from './appLogin';
import { cacheManager } from './queryClient';

// ---------------------------------------------------------------------------
// globals
// ---------------------------------------------------------------------------

/** Marker on axios configs so a replayed 401 does not refresh forever. */
export const SESSION_RETRY_FLAG = '_sessionRetry';

/** Stable toast id so parallel 401s collapse to one session-expired message. */
export const SESSION_EXPIRED_TOAST_ID = 'session-expired';

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

export const FORBIDDEN_ACTION_MESSAGE = "You don't have permission to perform this action.";

type SessionRetryConfig = InternalAxiosRequestConfig & {
  [SESSION_RETRY_FLAG]?: boolean;
};

export type SessionAuthHandlers = {
  /**
   * Force-refresh and return a bearer token for replaying the failed request.
   * Example: Auth0 cacheMode off → fresh ID/access token string.
   */
  refreshAuthToken: () => Promise<string | null>;
};

let handlers: SessionAuthHandlers | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let sessionExpiredInFlight: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// private methods
// ---------------------------------------------------------------------------

/**
 * Single-flight token refresh so parallel 401s share one Auth0 round-trip.
 *
 * Example input: two concurrent 401s → one refreshAuthToken() call.
 * Example output: both awaiters receive the same token string (or null).
 */
const _refreshAuthTokenSingleFlight = async (): Promise<string | null> => {
  if (!handlers) {
    return null;
  }
  if (!refreshInFlight) {
    refreshInFlight = handlers.refreshAuthToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Clear cached session state, toast once, and send the user to `/login`.
 *
 * Example: expired JWT after failed refresh → toast + redirectToAppLogin(returnTo).
 */
const _handleSessionExpired = async (): Promise<void> => {
  if (sessionExpiredInFlight) {
    return sessionExpiredInFlight;
  }

  sessionExpiredInFlight = (async () => {
    cacheManager.handleTokenExpiry();
    notifySessionExpired();
    const returnTo = `${window.location.pathname}${window.location.search}`;
    redirectToAppLogin(returnTo);
  })().finally(() => {
    sessionExpiredInFlight = null;
  });

  return sessionExpiredInFlight;
};

/**
 * On 401: refresh once and replay; on second 401 or failed refresh: session expired.
 *
 * Example: first 401 → new token → client.request(config) succeeds.
 * Example: refresh returns null → session-expired toast + redirect, then reject.
 */
const _handleUnauthorizedResponse = async (
  error: AxiosError,
  client: AxiosInstance
): Promise<unknown> => {
  const config = error.config as SessionRetryConfig | undefined;
  if (!config) {
    await _handleSessionExpired();
    return Promise.reject(error);
  }

  if (config[SESSION_RETRY_FLAG]) {
    await _handleSessionExpired();
    return Promise.reject(error);
  }

  const token = await _refreshAuthTokenSingleFlight();
  if (!token) {
    await _handleSessionExpired();
    return Promise.reject(error);
  }

  config[SESSION_RETRY_FLAG] = true;
  const headers = AxiosHeaders.from(config.headers ?? {});
  headers.set('Authorization', `Bearer ${token}`);
  config.headers = headers;
  return client.request(config);
};

/**
 * Axios response-error interceptor bound to a specific client instance.
 *
 * Example: 401 → refresh/replay; 403/other → reject unchanged (no re-auth).
 */
const _createUnauthorizedInterceptor = (client: AxiosInstance) => {
  return async (error: AxiosError): Promise<unknown> => {
    if (isUnauthorizedError(error)) {
      return _handleUnauthorizedResponse(error, client);
    }
    return Promise.reject(error);
  };
};

// ---------------------------------------------------------------------------
// public methods
// ---------------------------------------------------------------------------

/**
 * Register (or clear) Auth0/local callbacks used by the 401 interceptor.
 *
 * Example: AuthStateMonitor mounts → registerSessionAuthHandlers({ refreshAuthToken }).
 * Example: unmount → registerSessionAuthHandlers(null).
 */
export const registerSessionAuthHandlers = (next: SessionAuthHandlers | null): void => {
  handlers = next;
};

/** Test helper: clear in-flight locks and handlers. */
export const resetSessionAuthState = (): void => {
  handlers = null;
  refreshInFlight = null;
  sessionExpiredInFlight = null;
};

/**
 * Extract HTTP status from an axios-like error.
 *
 * Example: `{ response: { status: 401 } }` → 401
 * Example: `{ message: 'Network Error' }` → undefined
 */
export const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const status = (error as { response?: { status?: number } }).response?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * True when the API rejected the request as unauthenticated (dead/expired session).
 *
 * Example: status 401 → true
 * Example: status 403 (permission) → false
 */
export const isUnauthorizedError = (error: unknown): boolean => getHttpStatus(error) === 401;

/**
 * True when the caller is authenticated but not allowed (do not re-login).
 *
 * Example: status 403 → true
 * Example: status 401 → false
 */
export const isForbiddenError = (error: unknown): boolean => getHttpStatus(error) === 403;

/**
 * Show a single global session-expired toast (deduped by toastId).
 */
export const notifySessionExpired = (): void => {
  showToast('error', SESSION_EXPIRED_MESSAGE, {
    toastId: SESSION_EXPIRED_TOAST_ID,
  });
};

/**
 * Install 401 refresh/replay interceptors on default axios and apiClient.
 * Returns an eject function for cleanup (e.g. AuthStateMonitor unmount).
 *
 * Example: install → ActionCell axios 401 refreshes; eject → no longer handles 401.
 */
export const installSessionAuthInterceptors = (): (() => void) => {
  const axiosInterceptorId = axios.interceptors.response.use(
    response => response,
    _createUnauthorizedInterceptor(axios)
  );
  const apiClientInterceptorId = apiClient.interceptors.response.use(
    response => response,
    _createUnauthorizedInterceptor(apiClient)
  );

  return () => {
    axios.interceptors.response.eject(axiosInterceptorId);
    apiClient.interceptors.response.eject(apiClientInterceptorId);
  };
};
